import { motion } from 'framer-motion';
import { X, Check, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface ComparisonItem {
  problem: string;
  solution: string;
  improvement?: string;
}

interface BeforeAfterSliderProps {
  items: ComparisonItem[];
}

const BeforeAfterSlider = ({ items }: BeforeAfterSliderProps) => {
  return (
    <div className="relative">
      <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
        {/* Before - Traditional */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card className="p-10 h-full bg-gradient-to-br from-red-500/10 to-red-600/10 border-red-500/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl" />
            
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center border-2 border-red-500/40">
                <X className="w-8 h-8 text-red-400" />
              </div>
              <div>
                <h3 className="text-3xl font-bold">Before</h3>
                <p className="text-sm text-red-400 font-semibold">Traditional Approach</p>
              </div>
            </div>
            
            <ul className="space-y-5">
              {items.map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.15 }}
                  className="flex items-start gap-4 p-4 rounded-lg bg-background/50 border border-red-500/20"
                >
                  <X className="w-6 h-6 text-red-400 mt-1 flex-shrink-0" />
                  <div>
                    <span className="text-base text-muted-foreground leading-relaxed">{item.problem}</span>
                  </div>
                </motion.li>
              ))}
            </ul>
          </Card>
        </motion.div>

        {/* After - With Holionix */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card className="p-10 h-full bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl" />
            
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute top-6 right-6 gradient-brand text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg"
            >
              ✨ With Holionix
            </motion.div>
            
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-2xl gradient-brand flex items-center justify-center shadow-xl">
                <Check className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-bold">After</h3>
                <p className="text-sm text-green-400 font-semibold">AI-Powered Solution</p>
              </div>
            </div>
            
            <ul className="space-y-5">
              {items.map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.15 }}
                  className="flex items-start gap-4 p-4 rounded-lg bg-background/50 border border-green-500/20"
                >
                  <Check className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                  <div className="flex-grow">
                    <span className="text-base font-medium leading-relaxed block mb-2">{item.solution}</span>
                    {item.improvement && (
                      <div className="flex items-center gap-2 text-sm text-green-400 font-bold">
                        <TrendingUp className="w-4 h-4" />
                        {item.improvement}
                      </div>
                    )}
                  </div>
                </motion.li>
              ))}
            </ul>
          </Card>
        </motion.div>
      </div>

      {/* Center Arrow */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden md:block"
      >
        <div className="w-20 h-20 rounded-full gradient-brand flex items-center justify-center shadow-2xl border-4 border-background">
          <motion.div
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span className="text-3xl">→</span>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default BeforeAfterSlider;
